import { supabase } from "@/api/supabaseClient";
import { allowNextSignedIn, cancelExplicitSignIn } from "@/auth/webSignOutLatch";
import { queryParam } from "@/auth/linking";
import { routes } from "@/constants/routes";
import { useTheme } from "@/theme/ThemeProvider";
import { AppButton } from "@/ui/components/AppButton";
import { ErrorText } from "@/ui/components/ErrorText";
import { FloatingLabelInput } from "@/ui/components/FloatingLabelInput";
import { LinkText } from "@/ui/components/LinkText";
import * as Linking from "expo-linking";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useEffect, useState } from "react";
import { ScrollView, Text } from "react-native";

const MIN_PASSWORD = 8;

export default function RecoveryScreen() {
  const { theme } = useTheme();
  const router = useRouter();
  const params = useLocalSearchParams<{ token_hash?: string; type?: string }>();
  const [tokenHash, setTokenHash] = useState<string | null>(
    typeof params.token_hash === "string" ? params.token_hash : null,
  );
  const [verified, setVerified] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (tokenHash) return;
    let active = true;
    Linking.getInitialURL().then((url) => {
      const fromUrl = queryParam(url, "token_hash");
      if (active && fromUrl) setTokenHash(fromUrl);
    });
    const sub = Linking.addEventListener("url", ({ url }) => {
      const fromUrl = queryParam(url, "token_hash");
      if (fromUrl) setTokenHash(fromUrl);
    });
    return () => {
      active = false;
      sub.remove();
    };
  }, [tokenHash]);

  const confirmLink = async () => {
    if (!tokenHash || loading) return;
    setError(null);
    setLoading(true);
    try {
      allowNextSignedIn();
      const { error: verifyError } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: "recovery",
      });
      if (verifyError) throw verifyError;
      setVerified(true);
    } catch (e: unknown) {
      cancelExplicitSignIn();
      setError(e instanceof Error ? e.message : "Der Link konnte nicht bestätigt werden.");
    } finally {
      setLoading(false);
    }
  };

  const savePassword = async () => {
    setError(null);
    if (password !== confirm) {
      setError("Passwörter stimmen nicht überein.");
      return;
    }
    if (password.length < MIN_PASSWORD) {
      setError(`Passwort mindestens ${MIN_PASSWORD} Zeichen.`);
      return;
    }
    setLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;
      router.replace(routes.home);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Passwort konnte nicht gesetzt werden.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: theme.color.bg }}
      contentContainerStyle={{
        flexGrow: 1,
        justifyContent: "center",
        padding: 24,
        gap: 16,
        maxWidth: 400,
        alignSelf: "center",
        width: "100%",
        backgroundColor: theme.color.bg,
      }}
    >
      <Text style={{ color: theme.color.text, fontSize: theme.fontSize.title, fontWeight: "700" }}>
        Neues Passwort
      </Text>
      {!verified ? (
        <>
          <Text style={{ color: theme.color.muted, fontSize: theme.fontSize.body }}>
            Der Link aus der E-Mail wird erst bestätigt, wenn du den Button drückst. Danach bleibst
            du angemeldet und legst das neue Passwort fest.
          </Text>
          {!tokenHash ? (
            <Text style={{ color: theme.color.danger, fontSize: theme.fontSize.body }}>
              In dieser Adresse fehlt der Bestätigungscode. Öffne den Link aus der E-Mail erneut.
            </Text>
          ) : null}
          {error ? <ErrorText>{error}</ErrorText> : null}
          <AppButton onPress={confirmLink} disabled={!tokenHash || loading} loading={loading}>
            {loading ? "Wird geprüft…" : "Link bestätigen"}
          </AppButton>
          <LinkText onPress={() => router.replace(routes.forgotPassword)} color={theme.color.muted}>
            Neuen Link anfordern
          </LinkText>
        </>
      ) : (
        <>
          <Text style={{ color: theme.color.muted, fontSize: theme.fontSize.body }}>
            Lege das neue Passwort zweimal fest. Die Anmeldung bleibt bestehen.
          </Text>
          <FloatingLabelInput
            label="Neues Passwort"
            value={password}
            onChangeText={setPassword}
            secureTextEntry
          />
          <FloatingLabelInput
            label="Passwort bestätigen"
            value={confirm}
            onChangeText={setConfirm}
            secureTextEntry
          />
          {error ? <ErrorText>{error}</ErrorText> : null}
          <AppButton onPress={savePassword} disabled={loading} loading={loading}>
            {loading ? "Wird gespeichert…" : "Passwort speichern"}
          </AppButton>
        </>
      )}
    </ScrollView>
  );
}
